import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  CloudArrowDownIcon, 
  TrashIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  WifiIcon,
  SignalSlashIcon,
  ChartBarIcon,
  DocumentTextIcon,
  PlayIcon,
  BookOpenIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'
// import api from '../utils/api' // TODO: Update for new API structure

const OfflineMode = ({ onClose }) => {
  const [availablePackages, setAvailablePackages] = useState([])
  const [downloadedPackages, setDownloadedPackages] = useState([])
  const [storageStats, setStorageStats] = useState(null)
  const [updates, setUpdates] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(new Set())
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    loadOfflineData()
    
    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const loadOfflineData = async () => {
    try {
      setLoading(true)
      const [packages, downloads, stats, updateCheck] = await Promise.all([
        api.get('/offline/packages'),
        api.get('/offline/downloads'),
        api.get('/offline/storage'),
        api.get('/offline/updates').catch(() => ({ data: [] })) // Don't fail if updates check fails
      ])
      
      setAvailablePackages(packages.data)
      setDownloadedPackages(downloads.data)
      setStorageStats(stats.data)
      setUpdates(updateCheck.data)
    } catch (error) {
      console.error('Error loading offline data:', error)
    } finally {
      setLoading(false)
    }
  }

  const downloadPackage = async (packageId) => {
    try {
      setDownloading(prev => new Set([...prev, packageId]))
      
      const response = await api.post(`/offline/download/${packageId}`)
      
      // Store in service worker IndexedDB for robust offline access
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const messageChannel = new MessageChannel()
        
        navigator.serviceWorker.controller.postMessage({
          type: 'STORE_OFFLINE_PACKAGE',
          payload: {
            packageId,
            data: response.data,
            metadata: {
              downloadedAt: new Date().toISOString(),
              packageName: getPackageName(packageId)
            }
          }
        }, [messageChannel.port2])
        
        // Wait for service worker response
        await new Promise((resolve, reject) => {
          messageChannel.port1.onmessage = (event) => {
            if (event.data.type === 'STORE_OFFLINE_PACKAGE_SUCCESS') {
              resolve(event.data.result)
            } else if (event.data.type === 'STORE_OFFLINE_PACKAGE_ERROR') {
              reject(new Error(event.data.error))
            }
          }
        })
      } else {
        // Fallback to localStorage if service worker not available
        localStorage.setItem(`offline_package_${packageId}`, JSON.stringify(response.data))
      }
      
      // Refresh data
      await loadOfflineData()
      
      // Show success notification
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(registration => {
          registration.showNotification('Download Complete! 📦', {
            body: `Offline content for ${getPackageName(packageId)} is now available`,
            icon: '/icons/icon-192x192.png',
            badge: '/icons/badge-72x72.png',
            tag: 'offline-download',
            data: { packageId },
            actions: [
              { action: 'view-content', title: 'View Content' },
              { action: 'dismiss', title: 'Close' }
            ]
          })
        })
      }
      
    } catch (error) {
      console.error('Error downloading package:', error)
      alert('Failed to download package: ' + error.message)
    } finally {
      setDownloading(prev => {
        const newSet = new Set(prev)
        newSet.delete(packageId)
        return newSet
      })
    }
  }

  const deletePackage = async (packageId) => {
    if (!confirm('Are you sure you want to delete this offline content?')) {
      return
    }

    try {
      await api.delete(`/offline/downloads/${packageId}`)
      
      // Remove from service worker IndexedDB
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const messageChannel = new MessageChannel()
        
        navigator.serviceWorker.controller.postMessage({
          type: 'DELETE_OFFLINE_PACKAGE',
          payload: { packageId }
        }, [messageChannel.port2])
        
        // Wait for service worker response
        await new Promise((resolve, reject) => {
          messageChannel.port1.onmessage = (event) => {
            if (event.data.type === 'DELETE_OFFLINE_PACKAGE_SUCCESS') {
              resolve()
            } else if (event.data.type === 'DELETE_OFFLINE_PACKAGE_ERROR') {
              reject(new Error(event.data.error))
            }
          }
        })
      } else {
        // Fallback to localStorage cleanup
        localStorage.removeItem(`offline_package_${packageId}`)
      }
      
      // Refresh data
      await loadOfflineData()
      
    } catch (error) {
      console.error('Error deleting package:', error)
      alert('Failed to delete package: ' + error.message)
    }
  }

  const syncOfflineProgress = async () => {
    try {
      // Trigger background sync through service worker first
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const messageChannel = new MessageChannel()
        
        navigator.serviceWorker.controller.postMessage({
          type: 'SYNC_OFFLINE_PROGRESS'
        }, [messageChannel.port2])
        
        // Wait for service worker response
        await new Promise((resolve, reject) => {
          messageChannel.port1.onmessage = (event) => {
            if (event.data.type === 'SYNC_OFFLINE_PROGRESS_SUCCESS') {
              resolve()
            } else if (event.data.type === 'SYNC_OFFLINE_PROGRESS_ERROR') {
              reject(new Error(event.data.error))
            }
          }
          
          // Add timeout to prevent hanging
          setTimeout(() => reject(new Error('Sync timeout')), 10000)
        })
      }
      
      // Also call API directly as backup
      await api.post('/offline/sync')
      
      alert('✅ Offline progress synchronized successfully!')
    } catch (error) {
      console.error('Error syncing offline progress:', error)
      alert('⚠️ Failed to sync offline progress: ' + error.message)
    }
  }

  const checkForUpdates = async () => {
    try {
      // Trigger content update check through service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const messageChannel = new MessageChannel()
        
        navigator.serviceWorker.controller.postMessage({
          type: 'CHECK_CONTENT_UPDATES'
        }, [messageChannel.port2])
        
        // Wait for service worker response
        await new Promise((resolve, reject) => {
          messageChannel.port1.onmessage = (event) => {
            if (event.data.type === 'CHECK_CONTENT_UPDATES_SUCCESS') {
              resolve()
            } else if (event.data.type === 'CHECK_CONTENT_UPDATES_ERROR') {
              reject(new Error(event.data.error))
            }
          }
          
          // Add timeout to prevent hanging
          setTimeout(() => reject(new Error('Update check timeout')), 8000)
        })
      }
      
      // Also refresh local data to show any available updates
      await loadOfflineData()
      
    } catch (error) {
      console.error('Error checking for updates:', error)
      alert('⚠️ Failed to check for updates: ' + error.message)
    }
  }

  const getPackageName = (packageId) => {
    const pkg = availablePackages.find(p => p.id === packageId)
    return pkg ? pkg.name : packageId
  }

  const getPackageIcon = (type) => {
    switch (type) {
      case 'lessons': return BookOpenIcon
      case 'simulations': return PlayIcon
      case 'guides': return DocumentTextIcon
      case 'assets': return PhotoIcon
      default: return CloudArrowDownIcon
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50'
      case 'medium': return 'text-yellow-600 bg-yellow-50'
      case 'low': return 'text-green-600 bg-green-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const isPackageDownloaded = (packageId) => {
    return downloadedPackages.some(pkg => pkg.package_id === packageId)
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Loading offline content...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-6 text-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="relative">
                <CloudArrowDownIcon className="h-8 w-8" />
                {!isOnline && (
                  <SignalSlashIcon className="h-4 w-4 absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold">Offline Mode</h2>
                <p className="opacity-90 flex items-center gap-2">
                  {isOnline ? (
                    <>
                      <WifiIcon className="h-4 w-4" />
                      Online - Download content for offline use
                    </>
                  ) : (
                    <>
                      <SignalSlashIcon className="h-4 w-4" />
                      Offline - Using cached content
                    </>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex h-[calc(90vh-140px)]">
          {/* Storage Stats Sidebar */}
          <div className="w-80 border-r bg-gray-50 p-6 overflow-y-auto">
            {/* Storage Usage */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5" />
                Storage Usage
              </h3>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Downloaded</span>
                    <span className="font-semibold">{storageStats?.totalSizeMB || 0} MB</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${storageStats?.estimatedQuotaUsed || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {storageStats?.packageCount || 0} package{(storageStats?.packageCount || 0) !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              {isOnline && (
                <>
                  <button
                    onClick={loadOfflineData}
                    className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2 justify-center"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Refresh
                  </button>
                  
                  <button
                    onClick={checkForUpdates}
                    className="w-full bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 flex items-center gap-2 justify-center"
                  >
                    <CloudArrowDownIcon className="h-4 w-4" />
                    Check Updates
                  </button>
                </>
              )}
              
              {isOnline && downloadedPackages.length > 0 && (
                <button
                  onClick={syncOfflineProgress}
                  className="w-full bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center gap-2 justify-center"
                >
                  <CloudArrowDownIcon className="h-4 w-4" />
                  Sync Progress
                </button>
              )}

              <button
                onClick={() => api.post('/offline/clear-cache')}
                className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 flex items-center gap-2 justify-center"
              >
                <TrashIcon className="h-4 w-4" />
                Clear Cache
              </button>
            </div>

            {/* Updates Available */}
            {updates.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Updates Available</h4>
                <div className="space-y-2">
                  {updates.map(update => (
                    <div key={update.packageId} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-yellow-800">
                        {update.packageName}
                      </p>
                      <p className="text-xs text-yellow-600">
                        {update.status === 'outdated' ? 'Needs update' : 'New version available'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Downloaded Packages */}
            {downloadedPackages.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  Downloaded Content ({downloadedPackages.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {downloadedPackages.map((pkg) => {
                    const availablePkg = availablePackages.find(p => p.id === pkg.package_id)
                    const Icon = getPackageIcon(availablePkg?.type)
                    
                    return (
                      <div key={pkg.package_id} className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <Icon className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-green-900">
                                {getPackageName(pkg.package_id)}
                              </h4>
                              <p className="text-sm text-green-700">
                                {pkg.sizeMB} MB • Downloaded {new Date(pkg.downloaded_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => deletePackage(pkg.package_id)}
                            className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete offline content"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Available Packages */}
            <div>
              <h3 className="text-xl font-bold mb-4">Available for Download</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {availablePackages.map((pkg) => {
                  const Icon = getPackageIcon(pkg.type)
                  const isDownloaded = isPackageDownloaded(pkg.id)
                  const isDownloadingPkg = downloading.has(pkg.id)
                  
                  return (
                    <div key={pkg.id} className="bg-white border rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-blue-100 rounded-xl">
                            <Icon className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                            <p className="text-sm text-gray-600">{pkg.description}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(pkg.priority)}`}>
                          {pkg.priority}
                        </span>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Size:</span>
                          <span className="font-medium">{pkg.estimated_size_mb} MB</span>
                        </div>
                        {pkg.modules && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Modules:</span>
                            <span className="font-medium">{pkg.modules.length}</span>
                          </div>
                        )}
                        {pkg.simulation_count && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Simulations:</span>
                            <span className="font-medium">{pkg.simulation_count}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {isDownloaded ? (
                          <div className="flex-1 bg-green-100 text-green-700 px-4 py-2 rounded-lg flex items-center justify-center gap-2">
                            <CheckCircleIcon className="h-4 w-4" />
                            Downloaded
                          </div>
                        ) : isOnline ? (
                          <button
                            onClick={() => downloadPackage(pkg.id)}
                            disabled={isDownloadingPkg}
                            className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {isDownloadingPkg ? (
                              <>
                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <CloudArrowDownIcon className="h-4 w-4" />
                                Download
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="flex-1 bg-gray-100 text-gray-500 px-4 py-2 rounded-lg flex items-center justify-center gap-2">
                            <ExclamationCircleIcon className="h-4 w-4" />
                            Offline - Cannot Download
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Offline Usage Tips */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h4 className="font-semibold text-blue-900 mb-3">💡 Offline Mode Tips</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Downloaded content works completely offline - no internet required</li>
                <li>• Your progress is saved locally and synced when you're back online</li>
                <li>• Essential lessons and emergency guides are recommended for offline use</li>
                <li>• Regularly check for updates to keep your content current</li>
                <li>• Offline content is cached on your device and doesn't count against cloud storage</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default OfflineMode